import React, { ChangeEvent, useRef } from "react"
import styled, { css } from "styled-components"
import RadioButtonProps from "./radio-button.interface"
import { noop, uniqueId } from "lodash"
import { AppFunctionComponent } from "../../../../types"

const CustomRadioButton = styled.div<{ large?: boolean }>`
  display: flex;
  position: relative;
  width: 24px;
  height: 24px;
  margin: 2px;
  margin-right: 12px;
  box-sizing: border-box;

  flex-direction: row;
  align-items: center;
  justify-content: center;

  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.color.commerceBorder};

  cursor: pointer;

  transition: all 0.15s ease-in-out;

  ${({ large }) =>
    !large &&
    css`
      @media (min-width: ${({ theme }) =>
          theme.breakpoint.mobileHorizontal}px) {
        width: 16px;
        height: 16px;
      }
    `}

  &:after {
    content: "";

    width: 16px;
    height: 16px;
    display: block;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.color.commerceBlue};

    opacity: 0;
    visibility: hidden;

    transition: all 0.15s ease-in-out;
    ${({ large }) =>
      !large &&
      css`
        @media (min-width: ${({ theme }) =>
            theme.breakpoint.mobileHorizontal}px) {
          width: 10px;
          height: 10px;
        }
      `}
  }
`

const RadioButtonWrapper = styled.label`
  display: flex;
  align-items: center;
  flex-direction: row;
`

const RadioButtonInput = styled.input`
  width: 0;
  opacity: 0;

  &:focus {
    &:not(:checked) {
      + ${CustomRadioButton} {
        border-color: ${({ theme }) => theme.color.commerceBlue};
      }
    }
    &:checked {
      + ${CustomRadioButton} {
        border-color: ${({ theme }) => theme.color.commerceBlueTransparent};
      }
    }
  }
  &:checked {
    + ${CustomRadioButton} {
      &:after {
        opacity: 1;
        visibility: visible;
      }
    }
  }
`

const CustomLabel = styled.span`
  display: flex;
  align-items: center;
  flex-direction: row;

  font-size: 1rem;
  padding-top: 1px;
  font-weight: 300;
  line-height: 1em;
  font-family: ${({ theme }) => theme.font.basic};

  cursor: pointer;
`

const RadioButton: AppFunctionComponent<RadioButtonProps> = ({
  className,
  large,
  children,
  label,
  name,
  value,
  onChange,
  onMouseEnter = noop,
  buttonRef,
  ...props
}) => {
  const ref = useRef(buttonRef || null)
  const labelId = uniqueId()
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value)
  }
  const handleMouseEnter = () => {
    onMouseEnter(ref.current.value)
  }

  return (
    <RadioButtonWrapper className={className} data-testid="radio-button">
      <RadioButtonInput
        type="radio"
        name={name}
        value={value}
        onChange={handleChange}
        aria-labelledby={label ? labelId : undefined}
        {...props}
        ref={ref}
      />
      <CustomRadioButton onMouseEnter={handleMouseEnter} large={large} />
      {label && Boolean(label.length) ? (
        <CustomLabel id={labelId}>{label}</CustomLabel>
      ) : (
        children
      )}
    </RadioButtonWrapper>
  )
}

export default RadioButton
